import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useUpdateAtom } from 'jotai/utils';
import { ChangeEvent, forwardRef, useEffect, useState } from 'react';
import SearchSection from '../SearchSection';
import { IconRadioCircle20, IconRadioEmptyCircle20 } from 'components/base/Icon';
import List from 'components/base/List';
import Text from 'components/base/Text';
import useSearchFilter from 'components/search/hooks/useSearchFilter';
import { change_price_atom } from 'components/search/store/searchSheet';
import { border1, border2 } from 'styles';
import { isNumberOnly } from 'util/number';
import { Toast } from 'util/Toast';

type PriceItem = { name: string; count: number; min: number; max: number; selected: boolean };

const MAX_PRICE = 9_999_999;
const MIN_PRICE = 0;

// 전체 가격 필터
const ALL_PRICE = {
  min_price: -1,
  max_price: -1,
};

type CustomPriceType = 'min' | 'max';

interface Props {}

/**
 * 검색 필터 - 가격정보
 * @component
 */
const Price = forwardRef<HTMLElement, Props>((_, ref) => {
  const [is_enable_custom_search, setIsEnableCustomSearch] = useState(false);
  const [custom_price, setCustomPrice] = useState<Record<CustomPriceType, number>>({ min: MIN_PRICE, max: MAX_PRICE });
  const { price_list } = useSearchFilter();
  const changePrice = useUpdateAtom(change_price_atom);

  const price_range_list = price_list.filter((item) => !findCustomPriceSetting(item));
  const custom_input_price_info = price_list.find(findCustomPriceSetting);

  useEffect(() => {
    if (custom_input_price_info?.selected) {
      setCustomPrice({ min: custom_input_price_info.min, max: custom_input_price_info.max });
    }
  }, [custom_input_price_info]);

  const handleCustomPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const price_type = e.target.dataset.type as CustomPriceType;
    const value_without_comma = e.target.value.replace(/,/g, '');

    if (!isNumberOnly(value_without_comma)) {
      return;
    }
    const next_price_value = Math.min(Number(value_without_comma), MAX_PRICE);

    setCustomPrice((prev) => ({ ...prev, [price_type]: next_price_value }));
    setIsEnableCustomSearch(true);
  };

  const handlePriceRangeClick = ({ min, max, selected }: { min: number; max: number; selected: boolean }) => {
    const has_selected_all_price_filter = min === -1 && max === -1 && selected;
    if (has_selected_all_price_filter) {
      return;
    }

    const min_price = selected ? ALL_PRICE.min_price : min;
    const max_price = selected ? ALL_PRICE.max_price : max;
    setIsEnableCustomSearch(false);
    changePrice({ min: min_price, max: max_price });
    setCustomPrice({ min: min_price === -1 ? MIN_PRICE : min_price, max: max_price === -1 ? MAX_PRICE : max_price });
  };

  const handleSubmitPrice = () => {
    if (!is_enable_custom_search) {
      return;
    }

    if (custom_price.min > custom_price.max) {
      Toast.show('최소금액은 최대금액 이하로 설정해 주세요.');
      return;
    }
    changePrice({ min: custom_price.min, max: custom_price.max });
    setIsEnableCustomSearch(false);
  };

  return (
    <SearchSection title='가격' ref={ref}>
      <List>
        {price_range_list.map((item) => {
          return (
            <List.Item
              left={item.selected ? <IconRadioCircle20 /> : <IconRadioEmptyCircle20 />}
              css={price_style}
              key={item.name}
              title={item.name}
              title_style={
                item.selected
                  ? { variant: 'title5_medium', color: 'gray_active' }
                  : { variant: 'body2', color: 'gray_inactive' }
              }
              onClick={() => handlePriceRangeClick({ min: item.min, max: item.max, selected: item.selected })}
            />
          );
        })}
      </List>
      <SC.CustomPrice>
        <div className='price_range'>
          <input
            type='text'
            pattern='\d*'
            data-type='min'
            value={custom_price.min.toLocaleString('ko-KR')}
            onChange={handleCustomPriceChange}
          />
          <span className='line' />
          <input
            type='text'
            pattern='\d*'
            data-type='max'
            value={custom_price.max.toLocaleString('ko-KR')}
            onChange={handleCustomPriceChange}
          />
          <button>
            <Text
              variant='title5_medium'
              color={is_enable_custom_search ? 'pink_primary' : 'gray_disabled'}
              onClick={handleSubmitPrice}
            >
              적용
            </Text>
          </button>
        </div>
        <Text variant='body4_regular' color='gray_tertiary'>
          가격을 직접 입력할 수 있어요.
        </Text>
      </SC.CustomPrice>
    </SearchSection>
  );
});

export default Price;

function findCustomPriceSetting(price_item: PriceItem) {
  return price_item.name === 'custom';
}
const price_style = css`
  cursor: pointer;
`;

const SC = {
  CustomPrice: styled.div`
    margin-top: 8px;
    width: 100%;

    .price_range {
      width: 100%;
      display: flex;
      align-items: center;
    }

    input {
      flex: 1;
      height: 40px;
      border: 1px solid ${border2};
      border-radius: 4px;
      padding: 10px 12px;
      min-width: 0;
    }

    button {
      margin-left: 6px;
      width: 42px;
      height: 40px;
    }

    .line {
      width: 10px;
      height: 1.5px;
      margin: 0 6px;
      border-top: 1px solid ${border1};
    }
  `,
};
